import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, LoadingScreen, PageHeader, Table } from '../components/Ui';
import { useToast } from '../contexts/ToastContext';
import { useBusinessOrderMetrics, useBusinessOverview } from '../hooks/businessData';

function todayInputValue() {
  return new Date().toISOString().slice(0, 10);
}

function formatDuration(ms: number | null): string {
  if (ms === null) return 'Sin datos';
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `${hours} h ${remaining} min` : `${hours} h`;
}

function formatOrderStatus(status: string): string {
  return { pendiente: 'Pendiente', preparacion: 'En preparación', completado: 'Completado', cancelado: 'Cancelado', fantasma: 'Fantasma' }[status] || status;
}

function formatDeliveryType(type: string): string {
  return { delivery: 'Delivery', pickup: 'Retiro', local: 'En el local' }[type] || type;
}

export default function BusinessOverviewPage() {
  const { show } = useToast();
  const [metricsDate, setMetricsDate] = useState(todayInputValue());
  const { data, isLoading, isError, error } = useBusinessOverview();
  const { data: metrics, isError: isMetricsError, error: metricsError } = useBusinessOrderMetrics(metricsDate);

  useEffect(() => {
    if (isError) {
      show(error instanceof Error ? error.message : 'No se pudo cargar el resumen.', 'error');
    }
  }, [error, isError, show]);

  useEffect(() => {
    if (isMetricsError) {
      show(metricsError instanceof Error ? metricsError.message : 'No se pudo cargar el tablero diario.', 'error');
    }
  }, [isMetricsError, metricsError, show]);

  if (isLoading || !data) return <LoadingScreen />;
  const cards = [
    ['Sucursales activas', data.activeBranches, '⌖'],
    ['Productos activos', data.activeProducts, '☷'],
    ['Pedidos pendientes', data.pendingOrders, '▣'],
    ['En preparación', data.preparingOrders, '♨'],
    ['Completados hoy', data.completedToday, '✓'],
  ];
  return (
    <div>
      <PageHeader title="Resumen" description="Estado operativo del negocio en tiempo real." />
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-5">
        {cards.map(([label, value, icon]) => (
          <Card key={String(label)} className="min-h-[104px] p-3 sm:min-h-0 sm:p-5">
            <div className="text-xl sm:text-2xl">{icon}</div>
            <div className="mt-2 text-2xl font-black leading-none sm:mt-4 sm:text-3xl">{value}</div>
            <div className="mt-1 text-xs font-semibold leading-tight text-slate-500 sm:text-sm">{label}</div>
          </Card>
        ))}
      </div>
      <Card className="mt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h2 className="text-xl font-black">Tablero diario</h2><p className="mt-1 text-sm text-slate-500">Estados y tiempos promedio por fecha.</p></div>
          <input className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold" type="date" value={metricsDate} onChange={(event) => setMetricsDate(event.target.value || todayInputValue())} />
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {metrics && Object.entries(metrics.counts).map(([state, count]) => <div key={state} className="rounded-2xl bg-slate-50 p-4"><div className="text-2xl font-black">{count}</div><div className="mt-1 text-xs font-bold uppercase tracking-wider text-slate-500">{state}</div></div>)}
        </div>
        {metrics && <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-slate-100 p-4"><div className="text-sm font-bold text-slate-500">Recepción</div><div className="mt-1 text-xl font-black">{formatDuration(metrics.averageTimings.receptionMs)}</div></div>
          <div className="rounded-2xl border border-slate-100 p-4"><div className="text-sm font-bold text-slate-500">Cocina</div><div className="mt-1 text-xl font-black">{formatDuration(metrics.averageTimings.kitchenMs)}</div></div>
          <div className="rounded-2xl border border-slate-100 p-4"><div className="text-sm font-bold text-slate-500">Espera listo</div><div className="mt-1 text-xl font-black">{formatDuration(metrics.averageTimings.readyWaitMs)}</div></div>
          <div className="rounded-2xl border border-slate-100 p-4"><div className="text-sm font-bold text-slate-500">Reparto</div><div className="mt-1 text-xl font-black">{formatDuration(metrics.averageTimings.transitMs)}</div></div>
        </div>}
      </Card>
      <Card className="mt-6">
        <div><h2 className="text-xl font-black">Pedidos del día</h2><p className="mt-1 text-sm text-slate-500">Estado actual y tiempo transcurrido por estación.</p></div>
        <div className="mt-4">
          {metrics && metrics.orders.length > 0 ? (
            <Table>
              <Table.ScrollContainer>
                <Table.Content aria-label="Pedidos del día" className="min-w-[800px]">
                  <Table.Header>
                    <Table.Column isRowHeader>Pedido</Table.Column>
                    <Table.Column>Cliente</Table.Column>
                    <Table.Column>Tipo</Table.Column>
                    <Table.Column>Estado</Table.Column>
                    <Table.Column>Recepción</Table.Column>
                    <Table.Column>Cocina</Table.Column>
                    <Table.Column>Espera listo</Table.Column>
                    <Table.Column>Reparto</Table.Column>
                  </Table.Header>
                  <Table.Body>
                    {metrics.orders.map((order) => (
                      <Table.Row key={order.id}>
                        <Table.Cell className="font-black">#{String(order.correlation).padStart(2, '0')}</Table.Cell>
                        <Table.Cell className="font-semibold">{order.customerName}</Table.Cell>
                        <Table.Cell>{formatDeliveryType(order.deliveryType)}</Table.Cell>
                        <Table.Cell><span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold">{formatOrderStatus(order.status)}</span></Table.Cell>
                        <Table.Cell className="font-semibold">{formatDuration(order.timings.receptionMs)}</Table.Cell>
                        <Table.Cell className="font-semibold">{formatDuration(order.timings.kitchenMs)}</Table.Cell>
                        <Table.Cell className="font-semibold">{formatDuration(order.timings.readyWaitMs)}</Table.Cell>
                        <Table.Cell className="font-semibold">{formatDuration(order.timings.transitMs)}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
          ) : <p className="py-8 text-center text-sm font-semibold text-slate-500">No hay pedidos para esta fecha.</p>}
        </div>
      </Card>
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card className="bg-slate-950 text-white"><h2 className="text-xl font-black">Recepción</h2><p className="mt-2 text-sm leading-6 text-slate-300">Valida pedidos, contacta al cliente, edita ingredientes permitidos y envía a cocina.</p><Link to="/dashboard/reception"><Button variant="secondary" className="mt-5">Abrir recepción</Button></Link></Card>
        <Card><h2 className="text-xl font-black">Cocina</h2><p className="mt-2 text-sm leading-6 text-slate-500">Filtra por zona de preparación y marca cada producto hasta completar el pedido.</p><Link to="/dashboard/kitchen"><Button className="mt-5">Abrir cocina</Button></Link></Card>
      </div>
    </div>
  );
}
